import { Suspense, useState, useCallback, useEffect, useMemo } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { useFBX, OrbitControls, Center } from '@react-three/drei';
import { Label } from './Label';
import styles from './FBXViewer.module.css';

interface ModelProps {
	url: string;
	scale: number;
	position: [number, number, number];
	onLoad: () => void;
	responsiveMultiplier: number;
}

const Model = ({ url, scale, position, onLoad, responsiveMultiplier }: ModelProps) => {
	const fbxOriginal = useFBX(url);
	const fbx = useMemo(() => fbxOriginal.clone(true), [fbxOriginal]);
	const { viewport } = useThree();

	useEffect(() => {
		onLoad();
	}, [fbx, onLoad]);

	const responsiveScale =
		viewport.aspect < 1.2 ? scale * (viewport.aspect * responsiveMultiplier) : scale;

	return (
		<Center position={position}>
			<primitive object={fbx} scale={responsiveScale} />
		</Center>
	);
};

export interface FBXViewerProps {
	url: string;
	cameraZ: number;
	scale: number;
	modelPosition?: [number, number, number];
	labelPosition?: [number, number, number];
	title?: string;
	manufacturer?: string;
	subtitle?: string;
	autoRotate?: boolean;
	responsiveMultiplier?: number;
	labelMultiplier?: number;
}

export const FBXViewer = ({
	url,
	cameraZ,
	scale,
	modelPosition = [0, 0, 0],
	labelPosition = [0, -0.5, 11.8],
	title,
	manufacturer,
	subtitle,
	autoRotate = false,
	responsiveMultiplier = 0.85,
	labelMultiplier = 1.2,
}: FBXViewerProps) => {
	const [loaded, setLoaded] = useState(false);
	const handleLoad = useCallback(() => setLoaded(true), []);

	return (
		<div className={styles.wrapper}>
			{!loaded && <div className={styles.skeleton} />}
			<Canvas
				className={styles.canvas}
				camera={{ position: [0, 0, cameraZ], fov: 60 }}
				dpr={[1, 2]}
				gl={{ antialias: true, alpha: true }}
				style={{ opacity: loaded ? 1 : 0 }}
			>
				<ambientLight intensity={0.7} />
				<directionalLight position={[10, 10, 5]} intensity={1.2} />
				<directionalLight position={[-5, 5, -5]} intensity={0.3} />

				<Suspense fallback={null}>
					<group>
						<Model
							url={url}
							scale={scale}
							position={modelPosition}
							onLoad={handleLoad}
							responsiveMultiplier={responsiveMultiplier}
						/>
						{(title || manufacturer || subtitle) && (
							<ResponsiveLabel
								title={title ?? ''}
								manufacturer={manufacturer ?? ''}
								subtitle={subtitle ?? ''}
								position={labelPosition}
								labelMultiplier={labelMultiplier}
							/>
						)}
					</group>
				</Suspense>

				<OrbitControls
					enablePan={false}
					enableZoom={false}
					minPolarAngle={Math.PI / 2.5}
					maxPolarAngle={Math.PI / 2}
					autoRotate={autoRotate}
				/>
			</Canvas>
		</div>
	);
};

const ResponsiveLabel = ({ title, manufacturer, subtitle, position, labelMultiplier }: any) => {
	const { viewport } = useThree();
	const responsiveScale = viewport.aspect < 1 ? Math.min(1, viewport.aspect * labelMultiplier) : 1;

	return (
		<group scale={responsiveScale}>
			<Label title={title} manufacturer={manufacturer} subtitle={subtitle} position={position} />
		</group>
	);
};
